/**
 * Base API configuration and utilities
 */
import axios from 'axios';
import { recordBetaSignal } from '../lib/betaTelemetry';

// Resolved in one place; '' means same-origin, absent means a build the
// prebuild guard should have refused (see src/lib/backendUrl.js).
import { API_BASE_URL } from '../lib/backendUrl';

export { API_BASE_URL };

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors globally
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // Beta friction signal: a failed or feature-disabled request. Recorded
    // locally only — attached to a report solely when the user files one.
    try {
      const status = error.response?.status;
      const detailCode = error.response?.data?.detail?.code;
      recordBetaSignal(
        status === 503 && detailCode === 'not_enabled' ? 'feature-disabled' : 'request-failed',
        {
          status: status || 0,
          url: `${error.config?.method || ''} ${error.config?.url || ''}`.trim(),
          code: detailCode || undefined,
        }
      );
    } catch {
      /* telemetry must never break a request path */
    }
    return Promise.reject(error);
  }
);

export default apiClient;
